import type { UIMessage } from "ai";
import { z } from "zod";
import { onDemandCampaignCreateRequestV1Schema, type SocialPostType } from "@runcheetah/shared";

const campaignGenerationPreviewSchema = z.object({
  render: z.object({ component: z.literal("campaign_generation_confirmation") }),
  summary: z.string().trim().min(1).max(600).nullable().optional(),
  assetCount: z.number().int().min(1).optional(),
  hasExplicitAssetCount: z.boolean().optional(),
  formats: z.array(z.string()).optional(),
  firstScheduledFor: z.string().datetime().nullable().optional(),
  scheduleSpacingHours: z.number().int().min(1).nullable().optional(),
  request: onDemandCampaignCreateRequestV1Schema,
});

export type AssistantCampaignGenerationPreviewReference = {
  id: string;
  summary: string | null;
  assetCount: number;
  hasExplicitAssetCount: boolean;
  formats: SocialPostType[];
  firstScheduledFor: string | null;
  scheduleSpacingHours: number | null;
  request: z.infer<typeof onDemandCampaignCreateRequestV1Schema>;
};

export type AssistantCampaignGenerationChoice = "preview_first" | "all_assets";

export type AssistantCampaignGenerationProposalState =
  | { status: "active" }
  | { status: "cancelled" }
  | {
      status: "created";
      workerJobId: string;
      generationMode: AssistantCampaignGenerationChoice;
      runsRequired?: number;
      additionalRunsUsed?: number;
    };

export type StartedAssistantCampaignGeneration = AssistantCampaignGenerationPreviewReference & {
  proposalMessageId: string;
  state: Extract<AssistantCampaignGenerationProposalState, { status: "created" }>;
};

export const ASSISTANT_CAMPAIGN_GENERATION_STATE_CHANGE_EVENT =
  "assistant-campaign-generation-state-change";

const ACTIVE_PROPOSAL_STATE: AssistantCampaignGenerationProposalState = { status: "active" };

export function getAssistantCampaignRemainingAssetCount(
  request: AssistantCampaignGenerationPreviewReference["request"],
): number {
  return Math.max(request.items.length - 1, 0);
}

export function getAssistantCampaignGenerationPreviewReferences(
  message: Pick<UIMessage, "parts">,
): AssistantCampaignGenerationPreviewReference[] {
  const references: AssistantCampaignGenerationPreviewReference[] = [];

  for (const [index, part] of message.parts.entries()) {
    const candidate = part as {
      type?: string;
      state?: string;
      toolCallId?: string;
      output?: unknown;
    };

    if (
      candidate.type !== "tool-preview_campaign_creation" ||
      candidate.state !== "output-available"
    ) {
      continue;
    }

    const reference = parseAssistantCampaignGenerationPreviewReference(
      candidate.output,
      typeof candidate.toolCallId === "string" ? candidate.toolCallId : `campaign-${index}`,
    );
    if (reference) {
      references.push(reference);
    }
  }

  return references;
}

export function isAssistantCampaignGenerationProposalStale(
  messages: readonly Pick<UIMessage, "id" | "role">[],
  proposalMessageId: string,
) {
  const proposalMessageIndex = messages.findIndex((message) => message.id === proposalMessageId);
  if (proposalMessageIndex === -1) {
    return false;
  }

  return messages.slice(proposalMessageIndex + 1).some((message) => message.role === "user");
}

export function getAssistantCampaignGenerationProposalStorageKey(
  brandId: string,
  proposalId: string,
) {
  return `assistant-campaign-generation:${brandId}:${proposalId}`;
}

export function loadAssistantCampaignGenerationProposalState(
  key: string,
): AssistantCampaignGenerationProposalState {
  if (typeof window === "undefined") {
    return ACTIVE_PROPOSAL_STATE;
  }

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return ACTIVE_PROPOSAL_STATE;
    }

    const parsed = JSON.parse(raw) as AssistantCampaignGenerationProposalState;
    if (parsed.status === "active" || parsed.status === "cancelled") {
      return parsed;
    }

    if (
      parsed.status === "created" &&
      typeof parsed.workerJobId === "string" &&
      (parsed.generationMode === "preview_first" || parsed.generationMode === "all_assets")
    ) {
      return {
        ...parsed,
        ...(typeof parsed.additionalRunsUsed === "number" && parsed.additionalRunsUsed >= 0
          ? { additionalRunsUsed: parsed.additionalRunsUsed }
          : {}),
      };
    }
  } catch {
    return ACTIVE_PROPOSAL_STATE;
  }

  return ACTIVE_PROPOSAL_STATE;
}

export function saveAssistantCampaignGenerationProposalState(
  key: string,
  state: AssistantCampaignGenerationProposalState,
) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Losing persisted proposal state should not block generation.
  }

  window.dispatchEvent(new CustomEvent(ASSISTANT_CAMPAIGN_GENERATION_STATE_CHANGE_EVENT));
}

export function getStartedAssistantCampaignGenerations(input: {
  brandId: string;
  messages: readonly Pick<UIMessage, "id" | "role" | "parts">[];
}): StartedAssistantCampaignGeneration[] {
  const started: StartedAssistantCampaignGeneration[] = [];

  for (const message of input.messages) {
    if (message.role !== "assistant") {
      continue;
    }

    for (const reference of getAssistantCampaignGenerationPreviewReferences(message)) {
      const state = loadAssistantCampaignGenerationProposalState(
        getAssistantCampaignGenerationProposalStorageKey(input.brandId, reference.id),
      );

      if (state.status === "created") {
        started.push({ ...reference, proposalMessageId: message.id, state });
      }
    }
  }

  return started;
}

function parseAssistantCampaignGenerationPreviewReference(
  value: unknown,
  id: string,
): AssistantCampaignGenerationPreviewReference | null {
  const parsed = campaignGenerationPreviewSchema.safeParse(value);
  if (!parsed.success) {
    return null;
  }

  return {
    id,
    summary: parsed.data.summary?.trim() || null,
    assetCount: parsed.data.assetCount ?? parsed.data.request.items.length,
    hasExplicitAssetCount: parsed.data.hasExplicitAssetCount === true,
    formats: parsed.data.request.items.map((item) => item.postType),
    firstScheduledFor:
      parsed.data.firstScheduledFor ?? parsed.data.request.items[0]?.scheduledFor ?? null,
    scheduleSpacingHours: parsed.data.scheduleSpacingHours ?? null,
    request: parsed.data.request,
  };
}
